import * as React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, useMediaQuery } from '@mui/material';
import { useNotify } from 'react-admin';
import axiosInstance from '../../core/axiosConfig';
import { getBackendLanguage } from '../../core/i18n';
import { PageHeader } from '../page-header/PageHeader';
import { BackButton } from '../back-button/BackButton';
import { EmptyBox } from '../empty-box/EmptyBox';
import { DraggableList, type DraggableListRow } from './draggableList';
import * as Sentry from '@sentry/react';
import { pageGutterSx } from '../../core/theme/layout';

/**
 * Dish order for one specific category, reached from the category order
 * screen's "Dania" action. Shows only the dishes assigned to that category —
 * no other categories, no dishes from elsewhere in the menu.
 */
export const MenuItemOrderPage = () => {
  const { id, categoryId } = useParams();
  const routerLocation = useLocation();
  const navState = routerLocation.state as { locationName?: string; categoryName?: string } | null;
  const { t } = useTranslation();
  const notify = useNotify();
  const isMobile = useMediaQuery('(max-width:600px)');
  const backendLanguage = getBackendLanguage();

  const [items, setItems] = React.useState<DraggableListRow[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  // Navigating in from the category screen already carries the name; a direct
  // link or a page refresh doesn't, so fall back to fetching it separately.
  const [categoryName, setCategoryName] = React.useState(navState?.categoryName ?? '');

  const loadData = React.useCallback(async () => {
    if (!id || !categoryId) return;

    setIsLoading(true);
    try {
      const res = await axiosInstance.get('/menu-item/get/list', { params: { restaurantId: id, language: backendLanguage } });
      const menuItems: any[] = Array.isArray(res.data) ? res.data : (res.data?.menuItems ?? []);

      const filtered: DraggableListRow[] = menuItems
        .filter((item: any) => String(item.categoryId) === categoryId)
        .map((item: any) => ({ id: item.id, name: item.name }));

      setItems(filtered);
    } catch (e) {
      console.error('Failed to load category dishes', e);
      Sentry.captureException(e);
      notify(t('menuOrder.loadFailed'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [id, categoryId, backendLanguage, notify, t]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    if (categoryName || !categoryId) return;

    axiosInstance
      .get('/category/get', { params: { categoryId } })
      .then((res) => setCategoryName(res.data?.name ?? ''))
      .catch((e) => {
        console.error('Failed to load category name', e);
        Sentry.captureException(e);
      });
  }, [categoryId, categoryName]);

  const handlePersist = React.useCallback(
    async (orderedMenuItemIds: number[]) => {
      await axiosInstance.patch('/menu-item/reorder', {
        categoryId: Number(categoryId),
        orderedMenuItemIds,
      });
    },
    [categoryId],
  );

  return (
    <Box sx={{ pb: 'var(--space-8)' }}>
      <PageHeader back={<BackButton />} title={t('menuOrder.itemsTitle')} subtitle={categoryName} />
      <Box sx={pageGutterSx}>
        {isLoading ? null : items.length === 0 ? (
          <EmptyBox
            title={t('menuOrder.itemsEmptyBox.title')}
            description={t('menuOrder.itemsEmptyBox.description')}
            showCreate={false}
          />
        ) : (
          <DraggableList initialRows={items} onPersist={handlePersist} saveErrorMessage={t('menuOrder.saveFailed')} />
        )}
      </Box>
    </Box>
  );
};

export default MenuItemOrderPage;
